import type { FlameNode, HotMethod, SlowQuery, Service } from "@coremonit/types";
import { makeRng, int, pick } from "./rng";

/** Build a realistic-ish flamegraph tree. */
export function generateFlamegraph(seed = 42): FlameNode {
  const rng = makeRng(seed);
  const child = (name: string, value: number, depth = 0): FlameNode => {
    const kids: FlameNode[] = [];
    if (depth < 4 && value > 40) {
      const nKids = int(rng, 1, 4);
      let remaining = Math.floor(value * 0.9);
      for (let i = 0; i < nKids && remaining > 6; i++) {
        const v = Math.max(6, Math.floor(remaining / (nKids - i) * (0.5 + rng())));
        const clamped = Math.min(v, remaining);
        remaining -= clamped;
        kids.push(child(pick(rng, [
          "OrderService.finalize", "OrderService.priceLines", "InventoryClient.reserve",
          "PaymentClient.charge", "Postgres.query", "Redis.get", "Kafka.produce",
          "JSON.stringify", "Handler.serialize", "Middleware.auth", "GZip.compress",
          "TCPStream.write", "Buffer.alloc", "V8.gc",
        ]), clamped, depth + 1));
      }
    }
    return { name, value, ...(kids.length ? { children: kids } : {}) };
  };
  return child("main-http-server", 1000);
}

export function hotMethodsFor(_s: Service, seed = 42): HotMethod[] {
  const rng = makeRng(seed);
  return [
    { method: "OrderService.finalize",           pct: 22.4, samples: 1120, language: "Java" },
    { method: "Postgres.query.SELECT.orders",    pct: 14.1, samples: 705,  language: "SQL"  },
    { method: "InventoryClient.reserve",         pct: 11.6, samples: 580,  language: "Java" },
    { method: "PaymentClient.charge",            pct:  9.3, samples: 465,  language: "Java" },
    { method: "JSON.stringify",                  pct:  6.2, samples: 310,  language: "Node" },
    { method: "GZip.compress",                   pct:  4.8, samples: 240,  language: "Node" },
    { method: "V8.gc.MarkCompact",               pct:  3.6, samples: 180,  language: "Node" },
    { method: "Redis.get.session",               pct:  3.1, samples: 155,  language: "Redis" },
    { method: "OTel.exportSpans",                pct:  2.5, samples: 125,  language: "Java" },
    { method: "Handler.serialize",               pct:  1.9, samples: int(rng, 60, 100), language: "Node" },
  ];
}

export function slowQueriesFor(services: Service[], seed = 42): SlowQuery[] {
  const rng = makeRng(seed);
  return [
    { query: "SELECT o.*, li.* FROM orders o LEFT JOIN order_lines li ON …",   avgMs: 640, p95Ms: 1_820, count: 4_120, service: "order-service" },
    { query: "SELECT * FROM inventory WHERE sku = ANY($1) FOR UPDATE",         avgMs: 210, p95Ms:   680, count: 12_400, service: "inventory-service" },
    { query: "UPDATE sessions SET last_seen = now() WHERE user_id = $1",       avgMs:  95, p95Ms:   280, count: 88_320, service: "auth-service" },
    { query: "SELECT count(*) FROM audit_log WHERE ts > now() - '7 days'",     avgMs:  38, p95Ms:   120, count:    920, service: "reporting-service" },
    { query: "SELECT * FROM products WHERE embeddings <-> $1 LIMIT 20",        avgMs: 145, p95Ms:   410, count:  7_800, service: "recommendation-svc" },
  ];
}
